import type { ReactNode } from 'react';
import EmptyState from './EmptyState.js';
import Spinner from './Spinner.js';

/**
 * §13 — "temiz tablo düzeni". Tipli, jenerik tablo: kırılım tabloları (§13.1),
 * hedef listesi (§13.4) ve oturum listesi (§13.5) aynı bileşeni kullanır.
 *
 * Görsel dil referans panelin tablosu ile birebir: `.table-head` başlık şeridi,
 * 13px gövde metni, satır ayracı #f3f4f6, tıklanabilir satırda #fafafa vurgusu,
 * seçili satırda marka tonu #fff4ec.
 * Sayısal kolonlar `align: 'right'` + tabular-nums ile hizalanır.
 */

export interface TableColumn<T> {
  /** React anahtar ve kolon kimliği. */
  key: string;
  header: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Hücre içeriği; verilmezse satırdan `key` alanı okunup metne çevrilir. */
  render?: (row: T, index: number) => ReactNode;
  /** Kolon genişliği ipucu — ör. 'w-32'. */
  className?: string;
  /** Dar ekranlarda gizlenecek ikincil kolonlar için — ör. 'hidden md:table-cell'. */
  cellClassName?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  /** Satır kimliği — index'e düşmek yerine gerçek kimlik verin (session_id, id…). */
  rowKey: (row: T, index: number) => string | number;
  /** Satır tıklanabilirse imleç + klavye erişimi eklenir (§13.3 adım → oturumlar). */
  onRowClick?: (row: T, index: number) => void;
  /** Seçili satırı vurgular. */
  isRowActive?: (row: T, index: number) => boolean;
  loading?: boolean;
  /** Satır yokken gösterilen içerik; verilmezse sıkışık boş durum çizilir. */
  empty?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Uzun listelerde başlık satırı sabit kalsın. */
  stickyHeader?: boolean;
  className?: string;
}

const ALIGN: Record<NonNullable<TableColumn<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

function defaultCell<T>(row: T, key: string): ReactNode {
  const value = (row as Record<string, unknown>)[key];
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isRowActive,
  loading = false,
  empty,
  emptyTitle = 'Kayıt yok',
  emptyDescription,
  stickyHeader = false,
  className = '',
}: TableProps<T>) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Yükleniyor…" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-2">
        {empty ?? <EmptyState compact title={emptyTitle} description={emptyDescription} />}
      </div>
    );
  }

  const clickable = onRowClick !== undefined;

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full border-collapse text-[13px]">
        <thead className="table-head">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={`px-4 py-2.5 font-semibold ${ALIGN[col.align ?? 'left']} ${
                  // Sabit başlıkta zemin th'ye taşınmalı; aksi hâlde satırlar altından görünür.
                  stickyHeader ? 'sticky top-0 z-10 bg-[#fafafa]' : ''
                } ${col.className ?? ''} ${col.cellClassName ?? ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const active = isRowActive?.(row, index) === true;
            return (
              <tr
                key={rowKey(row, index)}
                onClick={clickable ? () => onRowClick(row, index) : undefined}
                onKeyDown={
                  clickable
                    ? (event) => {
                        // Klavye ile de satır açılabilmeli (erişilebilirlik).
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row, index);
                        }
                      }
                    : undefined
                }
                tabIndex={clickable ? 0 : undefined}
                aria-selected={clickable ? active : undefined}
                className={`border-t border-[#f3f4f6] transition-colors ${
                  clickable ? 'cursor-pointer' : ''
                } ${active ? 'bg-[#fff4ec]' : 'hover:bg-[#fafafa] focus:bg-[#fafafa]'}`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-2.5 text-[13px] text-[#374151] ${ALIGN[col.align ?? 'left']} ${
                      col.align === 'right' ? 'num' : ''
                    } ${col.cellClassName ?? ''}`}
                  >
                    {col.render ? col.render(row, index) : defaultCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
